import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import { Colors } from '../../themes';
import { PanelHeader } from '../crop-circle/Components/PanelHeader';
import { SlideAction, ViewMode } from './enums';
import {
  ColumnData,
  SlideLayoutContextType,
  SlideLayoutProps,
  SlidePanelRegistrar,
  SlidePanelSlot,
} from './interfaces';

const SlideLayoutContext = createContext<SlideLayoutContextType | null>(null);

export const useSlideLayout = () => {
  const context = useContext(SlideLayoutContext);
  if (!context) {
    throw new Error('useSlideLayout must be used within a SlideLayout');
  }
  return context;
};

// Safe version that returns null if used outside of a SlideLayout
export const useSlideLayoutSafe = () => {
  return useContext(SlideLayoutContext);
};

/**
 * Register a column for a slide slot from inside a child route element.
 *
 * Requires the enclosing `<SlideLayout>` to have `enableChildRoutes` set.
 * On mount / when `deps` change, the column is set. On unmount, it is cleared.
 * Pass `null` to explicitly un-register a slot (e.g. gated by a local flag)
 * without unmounting the component.
 */
export const useSlidePanel = (
  slot: SlidePanelSlot,
  column: ColumnData | null,
  deps: React.DependencyList
) => {
  const ctx = useOutletContext<SlidePanelRegistrar | null>();
  const setSlidePanel = ctx?.setSlidePanel;

  // Keep refs to the latest setter + slot so the unmount-only cleanup below
  // can clear the right slot without re-subscribing each render.
  const setSlidePanelRef = useRef(setSlidePanel);
  const slotRef = useRef(slot);
  useEffect(() => {
    setSlidePanelRef.current = setSlidePanel;
    slotRef.current = slot;
  }, [setSlidePanel, slot]);

  // Publish on mount / when deps change. No cleanup here — we deliberately
  // don't clear→re-set between renders because that briefly flips the slot
  // to null (extra parent render, risk of SINGLE↔DUAL flicker). Runs as a
  // layout effect so the shell's viewMode derivation sees the column in the
  // same commit the child route publishes it.
  useLayoutEffect(() => {
    if (!setSlidePanel) return;
    setSlidePanel(slot, column);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSlidePanel, slot, ...deps]);

  // Clear on unmount only, via the ref so a mid-lifetime setter swap doesn't
  // leave a stale slot populated. useLayoutEffect (not useEffect) so the
  // cleanup runs synchronously during commit, before a sibling route's
  // useLayoutEffect setup in the same commit. With useEffect this fired
  // after paint and clobbered whatever the newly-mounted sibling had just
  // published into the same slot (e.g. /assets/:id ↔ /assets/filters).
  useLayoutEffect(() => {
    return () => {
      setSlidePanelRef.current?.(slotRef.current, null);
    };
  }, []);
};

export const SlideLayout: React.FC<SlideLayoutProps> = ({
  leftColumn,
  rightColumn: rightColumnProp,
  thirdColumn: thirdColumnProp,
  fourthColumn: fourthColumnProp,
  viewMode: viewModeProp,
  onViewModeChange,
  transitionKey,
  enableChildRoutes,
  outletContext: outletContextExtras,
}) => {
  // Slot state fed by child routes via `useSlidePanel`. The shell's prop
  // takes priority — it represents an explicit, user-driven override (e.g.
  // an assets selection panel) that should eclipse whatever route is
  // currently mounted. The outlet column is the URL-driven baseline used
  // whenever the shell hasn't supplied an override. Shells that need the
  // route panel to come back when their override clears (e.g. AssetsShell
  // does this when `clearSelection` runs) just stop passing the prop.
  const [outletRightColumn, setOutletRightColumn] =
    useState<ColumnData | null>(null);
  const [outletThirdColumn, setOutletThirdColumn] =
    useState<ColumnData | null>(null);
  const [outletFourthColumn, setOutletFourthColumn] =
    useState<ColumnData | null>(null);

  const setSlidePanel = useCallback(
    (slot: SlidePanelSlot, column: ColumnData | null) => {
      if (slot === 'right') setOutletRightColumn(column);
      else if (slot === 'third') setOutletThirdColumn(column);
      else if (slot === 'fourth') setOutletFourthColumn(column);
    },
    []
  );

  const rightColumn = rightColumnProp ?? outletRightColumn;
  const thirdColumn = thirdColumnProp ?? outletThirdColumn;
  const fourthColumn = fourthColumnProp ?? outletFourthColumn;

  // Derive DUAL from the resolved rightColumn when the prop is absent, so
  // shells don't have to keep writing `rightColumn ? DUAL : SINGLE`.
  const viewMode =
    viewModeProp ?? (rightColumn ? ViewMode.DUAL : ViewMode.SINGLE);

  const animationDuration = 500;
  const [searchParams] = useSearchParams();
  const direction = searchParams.get('direction');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(viewMode);
  const currentViewModeRef = useRef(currentViewMode);
  currentViewModeRef.current = currentViewMode;
  const [columnTitles, setColumnTitles] = useState<{
    left?: string;
    right?: string;
    third?: string;
    fourth?: string;
  }>({});
  const [columnOverlays, setColumnOverlays] = useState<{
    left?: ReactNode;
    right?: ReactNode;
    third?: ReactNode;
    fourth?: ReactNode;
  }>({});
  const LEFT_COLUMN_WIDTH_PERCENTAGE = 45;
  const RIGHT_COLUMN_WIDTH_PERCENTAGE = 55;

  // Keep previous column content around during exit animations so it
  // doesn't unmount before the user can see the transition.
  const deferredRightColumn = useRef(rightColumn);
  if (rightColumn) {
    deferredRightColumn.current = rightColumn;
  }
  const deferredThirdColumn = useRef(thirdColumn);
  if (thirdColumn) {
    deferredThirdColumn.current = thirdColumn;
  }
  const deferredFourthColumn = useRef(fourthColumn);
  if (fourthColumn) {
    deferredFourthColumn.current = fourthColumn;
  }
  // Defense-in-depth for the concurrent-close gate in the 4th-column auto-hide
  // effect: reading from the ref guarantees the latest value regardless of
  // React batching semantics.
  const hasThirdColumnRef = useRef(!!thirdColumn);
  hasThirdColumnRef.current = !!thirdColumn;

  // Get current visible columns based on props (view mode only controls animations, not mounting)
  const visibleColumns = useMemo(
    () => ({
      left: leftColumn,
      right: deferredRightColumn.current || null,
    }),
    [leftColumn, rightColumn]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Animation values for smooth transitions
  const leftColumnTranslateX = useSharedValue(0);
  const rightColumnTranslateX = useSharedValue(0);
  const leftColumnOpacity = useSharedValue(1);
  const rightColumnOpacity = useSharedValue(1);
  const leftColumnWidth = useSharedValue(
    currentViewMode === ViewMode.SINGLE ? 100 : LEFT_COLUMN_WIDTH_PERCENTAGE
  );
  const rightColumnWidth = useSharedValue(
    currentViewMode === ViewMode.DUAL ? RIGHT_COLUMN_WIDTH_PERCENTAGE : 0
  );
  const thirdColumnOpacity = useSharedValue(0);
  const thirdColumnWidth = useSharedValue(0);
  const thirdColumnTranslateX = useSharedValue(100);
  const isThirdPanelVisibleRef = useRef(false);
  const thirdCleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const fourthColumnOpacity = useSharedValue(0);
  const fourthColumnWidth = useSharedValue(0);
  const fourthColumnTranslateX = useSharedValue(100);
  const isFourthPanelVisibleRef = useRef(false);
  const fourthCleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Prevents the prop-sync useEffect from canceling animations triggered internally
  const suppressPropSyncRef = useRef(false);

  const switchToSingleMode = useCallback(() => {
    suppressPropSyncRef.current = true;
    setCurrentViewMode(ViewMode.SINGLE);

    // Fade out right column and expand left
    rightColumnOpacity.value = withTiming(0, { duration: animationDuration });
    rightColumnWidth.value = withTiming(0, { duration: animationDuration });
    leftColumnWidth.value = withTiming(100, { duration: animationDuration });

    onViewModeChange?.(ViewMode.SINGLE);
  }, [onViewModeChange, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);

  const switchToSingleModeImmediate = useCallback(() => {
    setCurrentViewMode(ViewMode.SINGLE);
    rightColumnOpacity.value = 0;
    leftColumnWidth.value = 100;
    rightColumnWidth.value = 0;
    onViewModeChange?.(ViewMode.SINGLE);
  }, [onViewModeChange, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);

  const switchToDualMode = useCallback(() => {
    suppressPropSyncRef.current = true;
    setCurrentViewMode(ViewMode.DUAL);
    leftColumnWidth.value = 100;
    rightColumnWidth.value = 0;

    // Expand right column and shrink left
    leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
      duration: animationDuration,
    });
    rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
      duration: animationDuration,
    });
    rightColumnOpacity.value = withTiming(1, { duration: animationDuration });

    onViewModeChange?.(ViewMode.DUAL);
  }, [onViewModeChange, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);

  const slideToNextColumn = useCallback(() => {
    suppressPropSyncRef.current = true;
    // Fade out left column first
    leftColumnOpacity.value = withTiming(0, {
      duration: animationDuration / 2,
    });

    setTimeout(() => {
      // Then shift right column to take left position
      rightColumnTranslateX.value = withTiming(-100, {
        duration: animationDuration / 2,
      });
      rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration / 2,
      });
      suppressPropSyncRef.current = false;
    }, animationDuration / 2);
  }, [leftColumnOpacity, rightColumnTranslateX, rightColumnWidth]);

  const slideToPreviousColumn = useCallback(() => {
    suppressPropSyncRef.current = true;
    // Fade out left column first
    rightColumnOpacity.value = withTiming(0, {
      duration: animationDuration / 2,
    });

    setTimeout(() => {
      // Then shift right column to take left position
      leftColumnTranslateX.value = withTiming(80, {
        duration: animationDuration / 2,
      });
      leftColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration / 2,
      });
      suppressPropSyncRef.current = false;
    }, animationDuration / 2);
  }, [rightColumnOpacity, leftColumnTranslateX, leftColumnWidth]);

  const fadeInRightColumns = useCallback(() => {
    rightColumnOpacity.value = 0;
    rightColumnOpacity.value = withTiming(1, {
      duration: animationDuration / 2,
    });
  }, [rightColumnOpacity]);

  const toggleThirdPanel = useCallback(() => {
    if (!thirdColumn) return;

    if (!isThirdPanelVisibleRef.current) {
      // Show third panel — run all animations simultaneously. The panel
      // itself also slides in horizontally from off-screen right via
      // translateX so the transition reads as a proper "push from right".
      isThirdPanelVisibleRef.current = true;
      leftColumnOpacity.value = withTiming(0, { duration: animationDuration });
      leftColumnWidth.value = withTiming(0, { duration: animationDuration });
      rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });
      thirdColumnOpacity.value = withTiming(1, { duration: animationDuration });
      thirdColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });
      thirdColumnTranslateX.value = 100;
      thirdColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
    } else {
      // Hide third panel — run all animations simultaneously. Slide the
      // panel out to the right as it fades/collapses.
      isThirdPanelVisibleRef.current = false;
      thirdColumnOpacity.value = withTiming(0, { duration: animationDuration });
      thirdColumnWidth.value = withTiming(0, { duration: animationDuration });
      thirdColumnTranslateX.value = withTiming(100, {
        duration: animationDuration,
      });
      rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });
      leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });
      leftColumnOpacity.value = withTiming(1, { duration: animationDuration });
    }
  }, [
    thirdColumn,
    leftColumnOpacity,
    leftColumnWidth,
    rightColumnWidth,
    thirdColumnOpacity,
    thirdColumnWidth,
    thirdColumnTranslateX,
  ]);

  const executeSlideAction = useCallback(
    (action?: SlideAction) => {
      switch (action) {
        case SlideAction.SINGLE:
          switchToSingleMode();
          break;
        case SlideAction.DUAL:
          switchToDualMode();
          break;
        case SlideAction.NEXT:
          slideToNextColumn();
          break;
        case SlideAction.PREV:
          slideToPreviousColumn();
          break;
        default:
          fadeInRightColumns();
          break;
      }
    },
    [
      switchToSingleMode,
      switchToDualMode,
      slideToNextColumn,
      slideToPreviousColumn,
      fadeInRightColumns,
    ]
  );

  // Sync viewMode prop to internal state
  useEffect(() => {
    if (suppressPropSyncRef.current) {
      suppressPropSyncRef.current = false;
      return;
    }
    if (viewMode !== currentViewMode) {
      if (viewMode === ViewMode.DUAL) {
        // Wait for the new right column content before animating open.
        // Otherwise the panel animates in showing the previous asset's
        // stale content from `deferredRightColumn.current` (which isn't
        // refreshed until `rightColumn` is truthy — see the assignment
        // above), then swaps mid-animation once the new column lands.
        // When rightColumn arrives in a subsequent render, the effect
        // re-runs (rightColumn is in the deps) and fires the animation.
        if (!rightColumn) return;
        switchToDualMode();
      } else {
        if (!rightColumn && deferredRightColumn.current) {
          // rightColumn was removed — animate out, then clear deferred content
          switchToSingleMode();
          setTimeout(() => {
            deferredRightColumn.current = null;
          }, animationDuration);
        } else if (!rightColumn) {
          switchToSingleModeImmediate();
        } else {
          switchToSingleMode();
        }
      }
    }
  }, [
    viewMode,
    currentViewMode,
    rightColumn,
    switchToDualMode,
    switchToSingleMode,
    switchToSingleModeImmediate,
  ]);

  // Keep a stable ref to executeSlideAction to avoid re-firing on every render
  const executeSlideActionRef = useRef(executeSlideAction);
  useEffect(() => {
    executeSlideActionRef.current = executeSlideAction;
  });

  // Handle direction from URL params on load — only re-runs when direction actually changes
  useEffect(() => {
    const action = direction
      ? (direction.toLowerCase() as SlideAction)
      : undefined;

    executeSlideActionRef.current(action);
  }, [direction]);

  // When transitionKey changes in DUAL mode, animate the column transition
  // useLayoutEffect runs before browser paint to avoid flickering on initial frame
  const prevTransitionKey = useRef(transitionKey);
  useLayoutEffect(() => {
    if (transitionKey === prevTransitionKey.current) return;
    if (currentViewMode !== ViewMode.DUAL || !rightColumn) {
      prevTransitionKey.current = transitionKey;
      return;
    }

    const isGoingBack =
      transitionKey === undefined && prevTransitionKey.current !== undefined;
    prevTransitionKey.current = transitionKey;

    rightColumnOpacity.value = 0;

    if (isGoingBack) {
      // Reset widths while columns are hidden (slideToPreviousColumn may have altered them)
      leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      // Left column slides in from the left (activity list returns from left)
      leftColumnTranslateX.value = -100;
      leftColumnOpacity.value = 0;
      leftColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      leftColumnOpacity.value = withTiming(1, { duration: animationDuration });
      rightColumnOpacity.value = withDelay(
        animationDuration,
        withTiming(1, { duration: animationDuration / 2 })
      );
    } else {
      // Forward: left column (Issue) slides in from the right column position
      leftColumnTranslateX.value = 100;
      leftColumnOpacity.value = 0;
      leftColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      leftColumnOpacity.value = withTiming(1, { duration: animationDuration });
      rightColumnOpacity.value = withDelay(
        animationDuration,
        withTiming(1, { duration: animationDuration / 2 })
      );
    }
  }, [
    transitionKey,
    currentViewMode,
    rightColumn,
    leftColumnTranslateX,
    leftColumnOpacity,
    leftColumnWidth,
    rightColumnWidth,
    rightColumnOpacity,
  ]);

  // Auto-toggle third panel when thirdColumn prop appears/disappears.
  // Key the effect on the boolean presence so re-renders that only change
  // the thirdColumn object identity don't re-run this effect (which would
  // otherwise re-evaluate hadThird against a ref that was updated last run).
  const hasThirdColumn = !!thirdColumn;
  useEffect(() => {
    if (hasThirdColumn && !isThirdPanelVisibleRef.current) {
      // Cancel any pending cleanup from a previous close animation so that
      // spam-clicking to re-open within 500 ms doesn't blank the panel.
      if (thirdCleanupTimeoutRef.current) {
        clearTimeout(thirdCleanupTimeoutRef.current);
        thirdCleanupTimeoutRef.current = null;
      }
      isThirdPanelVisibleRef.current = true;
      leftColumnOpacity.value = withTiming(0, { duration: animationDuration });
      leftColumnWidth.value = withTiming(0, { duration: animationDuration });
      rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });
      thirdColumnOpacity.value = withTiming(1, { duration: animationDuration });
      thirdColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });
      thirdColumnTranslateX.value = 100;
      thirdColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
    } else if (!hasThirdColumn && isThirdPanelVisibleRef.current) {
      isThirdPanelVisibleRef.current = false;
      thirdColumnOpacity.value = withTiming(0, { duration: animationDuration });
      thirdColumnWidth.value = withTiming(0, { duration: animationDuration });
      thirdColumnTranslateX.value = withTiming(100, {
        duration: animationDuration,
      });
      leftColumnOpacity.value = withTiming(1, { duration: animationDuration });
      // Restore left/right widths based on the incoming view mode so we
      // don't clobber a concurrent switchToSingleMode (e.g. user clicks
      // Back on the middle panel while the third panel is still open —
      // we need to end up in SINGLE mode with the list at 100%, not
      // DUAL at 45/55). Use the viewMode prop rather than currentViewMode
      // state: the state update from switchToSingleMode may not have
      // landed yet when this effect's closure is captured.
      if (viewMode === ViewMode.DUAL) {
        rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
        leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
      } else {
        rightColumnWidth.value = withTiming(0, { duration: animationDuration });
        leftColumnWidth.value = withTiming(100, {
          duration: animationDuration,
        });
      }
      thirdCleanupTimeoutRef.current = setTimeout(() => {
        deferredThirdColumn.current = null;
        thirdCleanupTimeoutRef.current = null;
      }, animationDuration);
    }
  }, [
    hasThirdColumn,
    viewMode,
    leftColumnOpacity,
    leftColumnWidth,
    rightColumnWidth,
    thirdColumnOpacity,
    thirdColumnWidth,
    thirdColumnTranslateX,
  ]);

  // Auto-toggle fourth panel when fourthColumn prop appears/disappears.
  // The fourth column opens from the third-open state: it shifts the third
  // column from 55% to 45% and slides in at 55% from the right. The "middle"
  // right column (asset card) collapses to 0 width and fades out entirely.
  //
  // Invariant (see plan §F): this effect must NOT read or write any third-
  // column shared value, ref, or state key outside the gated
  // `if (hasThirdColumnRef.current)` block inside the close branch.
  const hasFourthColumn = !!fourthColumn;
  useEffect(() => {
    if (hasFourthColumn && !isFourthPanelVisibleRef.current) {
      // Dev warning for malformed consumers — the 4th column has no
      // toggleFourthPanel fallback, so forgetting showBackButton/onBackButtonPress
      // ships a stuck panel.
      if (
        process.env.NODE_ENV !== 'production' &&
        fourthColumn &&
        (!fourthColumn.showBackButton || !fourthColumn.onBackButtonPress)
      ) {
        console.warn(
          '[SlideLayout] fourthColumn must set showBackButton: true and onBackButtonPress — there is no toggleFourthPanel fallback.'
        );
      }

      // Cancel any pending cleanup from a previous close animation so that
      // spam-clicking the "+" button doesn't blank the panel mid-re-open.
      if (fourthCleanupTimeoutRef.current) {
        clearTimeout(fourthCleanupTimeoutRef.current);
        fourthCleanupTimeoutRef.current = null;
      }
      isFourthPanelVisibleRef.current = true;

      // Collapse the asset card (right column) to 0 width to make room for
      // third (45) + fourth (55). We deliberately do NOT touch
      // `rightColumnOpacity` here — leaving it at 1 means that when the
      // third panel later closes while fourth is also closing, the third-
      // close branch's `rightColumnWidth → 55` restore yields a visible
      // asset card without needing the third-close branch to touch opacity
      // (which would violate the strict-additive invariant from plan §F).
      // `width: 0` + `overflow: 'hidden'` on the Animated.View is enough
      // to hide the card during the open animation.
      rightColumnWidth.value = withTiming(0, { duration: animationDuration });

      // Shift the edit panel (third) from 55% to 45% to make room for groups
      // at 55%.
      thirdColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });

      // Slide the fourth in from the right.
      fourthColumnOpacity.value = withTiming(1, {
        duration: animationDuration,
      });
      fourthColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });
      fourthColumnTranslateX.value = 100;
      fourthColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
    } else if (!hasFourthColumn && isFourthPanelVisibleRef.current) {
      isFourthPanelVisibleRef.current = false;

      fourthColumnOpacity.value = withTiming(0, {
        duration: animationDuration,
      });
      fourthColumnWidth.value = withTiming(0, {
        duration: animationDuration,
      });
      fourthColumnTranslateX.value = withTiming(100, {
        duration: animationDuration,
      });

      // CRITICAL: only restore the third-open widths if third is still open.
      // When the user exits the edit panel entirely, both third AND fourth
      // auto-hide in the same commit; the third-column close branch above
      // already owns left/right widths and restores them based on `viewMode`.
      // We must not race it here. Read from the ref (not the closure-captured
      // hasFourthColumn deps) for defense-in-depth against any render-batching
      // surprises.
      if (hasThirdColumnRef.current) {
        rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
        thirdColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
      }

      // Clear stale title so a future 4th-panel mode doesn't flash the
      // previous title.
      setColumnTitles((prev) => ({ ...prev, fourth: undefined }));

      fourthCleanupTimeoutRef.current = setTimeout(() => {
        deferredFourthColumn.current = null;
        fourthCleanupTimeoutRef.current = null;
      }, animationDuration);
    }
  }, [
    hasFourthColumn,
    fourthColumn,
    rightColumnOpacity,
    rightColumnWidth,
    thirdColumnWidth,
    fourthColumnOpacity,
    fourthColumnWidth,
    fourthColumnTranslateX,
  ]);

  // Clear any pending third/fourth-column cleanup timeouts on SlideLayout
  // unmount so the callbacks can't fire into a dead component.
  useEffect(
    () => () => {
      if (thirdCleanupTimeoutRef.current) {
        clearTimeout(thirdCleanupTimeoutRef.current);
        thirdCleanupTimeoutRef.current = null;
      }
      if (fourthCleanupTimeoutRef.current) {
        clearTimeout(fourthCleanupTimeoutRef.current);
        fourthCleanupTimeoutRef.current = null;
      }
    },
    []
  );

  const onSlideChange = useCallback(
    (action: SlideAction): Promise<void> => {
      return new Promise((resolve) => {
        executeSlideAction(action);

        setTimeout(() => {
          resolve();
        }, animationDuration);
      });
    },
    [executeSlideAction]
  );

  const handleBackPress = async (column: 'left' | 'right') => {
    const columnData =
      column === 'left' ? visibleColumns.left : visibleColumns.right;

    if (columnData?.backButtonGuard) {
      const allowed = await columnData.backButtonGuard();

      if (!allowed) {
        return;
      }
    }

    if (columnData?.backButtonTransition) {
      await onSlideChange(columnData?.backButtonTransition);
    }

    if (columnData?.onBackButtonPress) {
      columnData.onBackButtonPress();
      return;
    }

    if (!columnData?.backButtonTransition) {
      if (currentViewMode === ViewMode.DUAL) {
        // Animate left column expanding to full width before navigating back
        await onSlideChange(SlideAction.SINGLE);
      } else {
        await onSlideChange(SlideAction.PREV);
      }
    }

    navigate(-1);
  };

  const setColumnTitle = useCallback(
    (
      column: 'left' | 'right' | 'third' | 'fourth',
      title: string | undefined
    ) => {
      setColumnTitles((prev) => ({ ...prev, [column]: title }));
    },
    []
  );

  const setColumnOverlay = useCallback(
    (
      column: 'left' | 'right' | 'third' | 'fourth',
      overlay: ReactNode | null
    ) => {
      setColumnOverlays((prev) => ({
        ...prev,
        [column]: overlay ?? undefined,
      }));
    },
    []
  );

  const leftColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'left' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('left', title),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('left', overlay),
      onSlideChange,
      toggleThirdPanel,
    }),
    [setColumnTitle, setColumnOverlay, onSlideChange, toggleThirdPanel]
  );

  const rightColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'right' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('right', title),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('right', overlay),
      onSlideChange,
      toggleThirdPanel,
    }),
    [setColumnTitle, setColumnOverlay, onSlideChange, toggleThirdPanel]
  );

  const thirdColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'third' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('third', title),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('third', overlay),
      onSlideChange,
      toggleThirdPanel,
    }),
    [setColumnTitle, setColumnOverlay, onSlideChange, toggleThirdPanel]
  );

  const fourthColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'fourth' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('fourth', title),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('fourth', overlay),
      onSlideChange,
      toggleThirdPanel,
    }),
    [setColumnTitle, setColumnOverlay, onSlideChange, toggleThirdPanel]
  );

  // Outlet context exposed to child route elements — includes the slide-panel
  // registrar plus any app-specific fields the shell wants to share with its
  // child routes. `setSlidePanel` is placed last so a consumer can't
  // accidentally override it via `outletContextExtras`.
  const outletContextValue = useMemo(
    () => ({ ...outletContextExtras, setSlidePanel }),
    [outletContextExtras, setSlidePanel]
  );

  const leftColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${leftColumnTranslateX.value}%` }],
    opacity: leftColumnOpacity.value,
    width: `${leftColumnWidth.value}%`,
  }));

  const rightColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${rightColumnTranslateX.value}%` }],
    opacity: rightColumnOpacity.value,
    width: `${rightColumnWidth.value}%`,
    overflow: 'hidden',
  }));

  const thirdColumnAnimatedStyle = useAnimatedStyle(() => ({
    opacity: thirdColumnOpacity.value,
    width: `${thirdColumnWidth.value}%`,
    transform: [{ translateX: `${thirdColumnTranslateX.value}%` }],
    overflow: 'hidden',
  }));

  const fourthColumnAnimatedStyle = useAnimatedStyle(() => ({
    opacity: fourthColumnOpacity.value,
    width: `${fourthColumnWidth.value}%`,
    transform: [{ translateX: `${fourthColumnTranslateX.value}%` }],
    overflow: 'hidden',
  }));

  const rightColumnPointerStyle =
    currentViewMode !== ViewMode.DUAL ? styles.noPointerEvents : null;
  const thirdColumnPointerStyle = !deferredThirdColumn.current
    ? styles.noPointerEvents
    : null;
  const fourthColumnPointerStyle = !deferredFourthColumn.current
    ? styles.noPointerEvents
    : null;

  return (
    <View style={styles.container}>
      {/* Child route elements registered via `useSlidePanel` render null and
          only publish their column into the outlet state above. Rendered
          here rather than inside a column's mainComponent so SlideLayout
          owns the registration plumbing end-to-end. Wrapped in the left
          column's SlideLayoutContext provider so child routes can still
          call `useSlideLayout()` (e.g. for `onSlideChange`) — matching the
          previous behavior when the Outlet lived inside the left column. */}
      {enableChildRoutes && (
        <SlideLayoutContext.Provider value={leftColumnContextValue}>
          <Outlet context={outletContextValue} />
        </SlideLayoutContext.Provider>
      )}
      <View style={styles.columnsContainer} testID="slide-layout">
        {/* Left Column */}
        {visibleColumns.left && (
          <SlideLayoutContext.Provider value={leftColumnContextValue}>
            <Animated.View
              style={[styles.leftColumn, leftColumnAnimatedStyle]}
              testID="slide-column-left"
            >
              <View style={styles.columnContent}>
                {visibleColumns.left.showHeader !== false && (
                  <PanelHeader
                    title={
                      visibleColumns.left.rawTitle ??
                      t(columnTitles.left ?? visibleColumns.left.title)
                    }
                    showBackButton={visibleColumns.left.showBackButton}
                    onBackPress={() => handleBackPress('left')}
                    rightActions={visibleColumns.left.headerComponent}
                    backButtonTestID="slide-back-left"
                  />
                )}
                <View style={styles.sectionContent}>
                  {visibleColumns.left.disableScroll ? (
                    visibleColumns.left.mainComponent
                  ) : (
                    <ScrollView
                      style={styles.scrollContainer}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {visibleColumns.left.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.left && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.left}
                  </View>
                )}
              </View>
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}

        {/* Right Column - always mounted, visibility controlled by animations */}
        <SlideLayoutContext.Provider value={rightColumnContextValue}>
          <Animated.View
            style={[rightColumnAnimatedStyle, rightColumnPointerStyle]}
            testID="slide-column-right"
          >
            {visibleColumns.right && (
              <View style={styles.columnContent}>
                {visibleColumns.right.showHeader !== false && (
                  <PanelHeader
                    title={
                      visibleColumns.right.rawTitle ??
                      t(columnTitles.right ?? visibleColumns.right.title)
                    }
                    showBackButton={visibleColumns.right.showBackButton}
                    onBackPress={() => handleBackPress('right')}
                    rightActions={visibleColumns.right.headerComponent}
                    backButtonTestID="slide-back-right"
                  />
                )}
                <View style={styles.sectionContent}>
                  {visibleColumns.right.disableScroll ? (
                    visibleColumns.right.mainComponent
                  ) : (
                    <ScrollView
                      style={styles.scrollContainer}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {visibleColumns.right.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.right && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.right}
                  </View>
                )}
              </View>
            )}
          </Animated.View>
        </SlideLayoutContext.Provider>

        {/* Third Column - always mounted when provided, visibility controlled by animations */}
        <SlideLayoutContext.Provider value={thirdColumnContextValue}>
          <Animated.View
            style={[
              styles.thirdColumn,
              thirdColumnAnimatedStyle,
              thirdColumnPointerStyle,
            ]}
            testID="slide-column-third"
          >
            {deferredThirdColumn.current && (
              <View style={styles.columnContent}>
                {deferredThirdColumn.current.showHeader !== false && (
                  <PanelHeader
                    title={
                      deferredThirdColumn.current.rawTitle ??
                      t(
                        columnTitles.third ??
                          deferredThirdColumn.current.title
                      )
                    }
                    showBackButton={deferredThirdColumn.current.showBackButton}
                    onBackPress={() => {
                      if (deferredThirdColumn.current?.onBackButtonPress) {
                        deferredThirdColumn.current.onBackButtonPress();
                      } else if (isThirdPanelVisibleRef.current) {
                        toggleThirdPanel();
                      }
                    }}
                    rightActions={deferredThirdColumn.current.headerComponent}
                    backButtonTestID="slide-back-third"
                  />
                )}
                <View style={styles.sectionContent}>
                  {deferredThirdColumn.current?.disableScroll ? (
                    deferredThirdColumn.current.mainComponent
                  ) : (
                    <ScrollView
                      style={styles.scrollContainer}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {deferredThirdColumn.current?.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.third && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.third}
                  </View>
                )}
              </View>
            )}
          </Animated.View>
        </SlideLayoutContext.Provider>

        {/* Fourth Column - always mounted when provided, visibility controlled by animations */}
        <SlideLayoutContext.Provider value={fourthColumnContextValue}>
          <Animated.View
            style={[
              styles.fourthColumn,
              fourthColumnAnimatedStyle,
              fourthColumnPointerStyle,
            ]}
            testID="slide-column-fourth"
          >
            {deferredFourthColumn.current && (
              <View style={styles.columnContent}>
                {deferredFourthColumn.current.showHeader !== false && (
                  <PanelHeader
                    title={
                      deferredFourthColumn.current.rawTitle ??
                      t(
                        columnTitles.fourth ??
                          deferredFourthColumn.current.title
                      )
                    }
                    showBackButton={deferredFourthColumn.current.showBackButton}
                    onBackPress={() => {
                      deferredFourthColumn.current?.onBackButtonPress?.();
                    }}
                    rightActions={deferredFourthColumn.current.headerComponent}
                    backButtonTestID="slide-back-fourth"
                  />
                )}
                <View style={styles.sectionContent}>
                  {deferredFourthColumn.current?.disableScroll ? (
                    deferredFourthColumn.current.mainComponent
                  ) : (
                    <ScrollView
                      style={styles.scrollContainer}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {deferredFourthColumn.current?.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.fourth && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.fourth}
                  </View>
                )}
              </View>
            )}
          </Animated.View>
        </SlideLayoutContext.Provider>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  columnsContainer: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  leftColumn: {
    paddingRight: 16,
  },
  thirdColumn: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  fourthColumn: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  columnContent: {
    flex: 1,
  },
  sectionContent: {
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: Colors.brand.white,
    flexGrow: 1,
    overflow: 'hidden',
    height: 600,
    flex: 1,
  },
  columnOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  noPointerEvents: {
    pointerEvents: 'none',
  },
});
